import {FrameDebouncer} from './debouncer'

const defaultMaxSpeedY = 0.75
const defaultPower = 0.5
const windowThreshold = 3

interface NativeDragScrollParams {
  scrollableEl: HTMLElement
  thresholdY?: number
  maxSpeedY?: number
  /**
   * If <1 fast at start, slow at end; if > 1 slow at start, fast at end
   */
  powerY?: number
  allowOutside?: boolean
}

/**
 * Mouse events are not fired when using a native drag, so this implementation
 * uses `dragover` and `dragend` events along with some hacks to drag scroll the
 * provided container.
 */
export class NativeDragScroll {
  static start(params: NativeDragScrollParams) {
    const scrollableEl = params.scrollableEl
    const {thresholdY: maxThresholdY = Number.POSITIVE_INFINITY} = params
    const {maxSpeedY = defaultMaxSpeedY} = params
    const {powerY = defaultPower} = params
    const {allowOutside = false} = params

    let speed: number
    const frameDebouncer = new FrameDebouncer()
    frameDebouncer.everyFrame(dt => {
      const delta = speed * dt
      delta && (scrollableEl.scrollTop += delta)
    })

    let fallbackStopApplied = false

    const mouseMove = (e: DragEvent) => {
      // https://issuetracker.google.com/issues/327244265
      // `dragend` is not registered when dragging outside the window and dropping.
      // Mouse events are not fired when dragging, so consider drag stopped when
      // mouse events are back again.
      if (!fallbackStopApplied) {
        document.addEventListener('mousemove', stop, {
          capture: true,
          passive: true,
          once: true
        })
        fallbackStopApplied = true
      }

      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = 'none'
      }

      // Prevent scrolling when a pointer is almost outside the window;
      // see above for why.
      const isAlmostOutsideWindow =
        e.clientX <= windowThreshold ||
        e.clientX >= window.innerWidth - windowThreshold ||
        e.clientY <= windowThreshold ||
        e.clientY >= window.innerHeight - windowThreshold
      if (isAlmostOutsideWindow) {
        speed = 0
        return
      }

      // Prevent drag scroll when a pointer is outside the scrollable
      const rect = scrollableEl.getBoundingClientRect()

      const isOutsideScrollable =
        e.clientX <= rect.left ||
        e.clientX >= rect.right ||
        e.clientY <= rect.top ||
        e.clientY >= rect.bottom
      if (!allowOutside && isOutsideScrollable) {
        speed = 0
        return
      }

      const thresholdY = Math.min(maxThresholdY, rect.height / 2)
      if (e.clientY - rect.top < thresholdY) {
        const ds = Math.max(e.clientY - rect.top, 0)
        const p = Math.pow(1 - ds / thresholdY, powerY)
        speed = -maxSpeedY * p
      } else if (rect.bottom - e.clientY < thresholdY) {
        const ds = Math.max(rect.bottom - e.clientY, 0)
        const p = Math.pow(1 - ds / thresholdY, powerY)
        speed = maxSpeedY * p
      } else {
        speed = 0
      }
    }

    const stop = () => {
      document.removeEventListener('dragover', mouseMove, {capture: true})
      document.removeEventListener('dragend', stop, {capture: true})
      document.removeEventListener('mousemove', stop, {capture: true})
      frameDebouncer.clear()
    }

    document.addEventListener('dragover', mouseMove, {
      capture: true,
      passive: true
    })

    document.addEventListener('dragend', stop, {
      capture: true,
      passive: true,
      once: true
    })
  }
}
